import {
  coordinateKey,
  WEATHER_FORECAST_DAYS,
  WEATHER_TIME_ZONE,
} from "../domain/weather";
import type { WeatherCoordinates, WeatherHourData } from "../domain/weather";

export const WEATHER_CACHE_TTL_MS = 15 * 60 * 1000;

interface CachedForecast {
  expiresAt: number;
  forecast: LocationWeatherData;
}

export interface LocationWeatherData {
  hours: WeatherHourData[];
  dayPrecipitationByDate: Map<string, number | null>;
}

export interface LocationForecasts {
  byCoordinate: Map<string, LocationWeatherData>;
  expiresAt: number;
}

interface LoadOptions {
  signal?: AbortSignal;
  now?: number;
  fetchImpl?: typeof fetch;
}

const cache = new Map<string, CachedForecast>();

export async function loadLocationForecasts(
  coordinates: WeatherCoordinates[],
  options: LoadOptions = {},
): Promise<LocationForecasts> {
  const now = options.now ?? Date.now();
  const uniqueCoordinates = Array.from(
    new Map(
      coordinates.map((location) => [coordinateKey(location), location]),
    ).values(),
  );
  const missing = uniqueCoordinates.filter((location) => {
    const cached = cache.get(coordinateKey(location));
    return !cached || cached.expiresAt <= now;
  });

  if (missing.length) {
    const response = await (options.fetchImpl ?? fetch)(
      buildForecastUrl(missing),
      options.signal ? { signal: options.signal } : undefined,
    );
    if (!response.ok) {
      throw new Error(`Weather request failed with status ${response.status}.`);
    }
    const payload: unknown = await response.json();
    const locations = parseLocations(payload, missing.length);
    const expiresAt = now + WEATHER_CACHE_TTL_MS;
    missing.forEach((location, index) => {
      const forecast = locations[index];
      if (!forecast) throw new Error("Weather response omitted a location.");
      cache.set(coordinateKey(location), { expiresAt, forecast });
    });
  }

  const byCoordinate = new Map<string, LocationWeatherData>();
  let expiresAt = Number.POSITIVE_INFINITY;
  for (const location of uniqueCoordinates) {
    const key = coordinateKey(location);
    const cached = cache.get(key);
    if (!cached) throw new Error("Weather data was not cached after loading.");
    byCoordinate.set(key, cached.forecast);
    expiresAt = Math.min(expiresAt, cached.expiresAt);
  }
  return {
    byCoordinate,
    expiresAt: Number.isFinite(expiresAt)
      ? expiresAt
      : now + WEATHER_CACHE_TTL_MS,
  };
}

export function resetWeatherCache(): void {
  cache.clear();
}

function buildForecastUrl(coordinates: WeatherCoordinates[]): string {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set(
    "latitude",
    coordinates.map((location) => location.latitude).join(","),
  );
  url.searchParams.set(
    "longitude",
    coordinates.map((location) => location.longitude).join(","),
  );
  url.searchParams.set(
    "hourly",
    [
      "temperature_2m",
      "apparent_temperature",
      "precipitation_probability",
      "precipitation",
      "weather_code",
      "wind_speed_10m",
      "wind_gusts_10m",
      "wind_direction_10m",
    ].join(","),
  );
  url.searchParams.set("forecast_days", String(WEATHER_FORECAST_DAYS));
  url.searchParams.set("daily", "precipitation_sum");
  url.searchParams.set(
    "timezone",
    coordinates.map(() => WEATHER_TIME_ZONE).join(","),
  );
  return url.toString();
}

function parseLocations(
  payload: unknown,
  expectedLocations: number,
): LocationWeatherData[] {
  const locations = expectedLocations === 1 ? [payload] : payload;
  if (!Array.isArray(locations) || locations.length !== expectedLocations) {
    throw new Error("Weather response contained an unexpected location count.");
  }
  return locations.map(parseLocation);
}

function parseLocation(value: unknown): LocationWeatherData {
  if (!isRecord(value) || !isRecord(value.hourly)) {
    throw new Error("Weather response did not include hourly data.");
  }
  const hourly = value.hourly;
  if (!Array.isArray(hourly.time)) {
    throw new Error("Weather response did not include hourly timestamps.");
  }
  const hours = hourly.time.flatMap((time, index) => {
    if (typeof time !== "string") return [];
    return [
      {
        time,
        temperature: numberAt(hourly.temperature_2m, index),
        apparentTemperature: numberAt(hourly.apparent_temperature, index),
        precipitationProbability: numberAt(
          hourly.precipitation_probability,
          index,
        ),
        precipitation: numberAt(hourly.precipitation, index),
        weatherCode: numberAt(hourly.weather_code, index),
        windSpeed: numberAt(hourly.wind_speed_10m, index),
        windGusts: numberAt(hourly.wind_gusts_10m, index),
        windDirection: numberAt(hourly.wind_direction_10m, index),
      },
    ];
  });
  const dayPrecipitationByDate = new Map<string, number | null>();
  const daily = value.daily;
  if (isRecord(daily) && Array.isArray(daily.time)) {
    daily.time.forEach((time, index) => {
      if (typeof time !== "string") return;
      dayPrecipitationByDate.set(
        time,
        numberAt(daily.precipitation_sum, index),
      );
    });
  }
  return { hours, dayPrecipitationByDate };
}

function numberAt(value: unknown, index: number): number | null {
  if (!Array.isArray(value)) return null;
  const candidate: unknown = value[index];
  return typeof candidate === "number" && Number.isFinite(candidate)
    ? candidate
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
